import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import Spinner from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "dark";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  pending?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-white border border-brand-600 hover:bg-brand-700 hover:border-brand-700 active:bg-brand-800 shadow-sm shadow-brand-900/15",
  secondary:
    "bg-white text-ink-700 border border-ink-200 hover:bg-ink-50 hover:border-ink-300 active:bg-ink-100",
  ghost:
    "bg-transparent text-ink-700 border border-transparent hover:bg-ink-100/70 active:bg-ink-100",
  danger:
    "bg-red-600 text-white border border-red-600 hover:bg-red-700 hover:border-red-700 active:bg-red-800 shadow-sm shadow-red-900/20",
  dark:
    "bg-ink-800 text-white border border-ink-800 hover:bg-ink-900 active:bg-ink-900 shadow-sm shadow-ink-900/20",
};

const SIZE: Record<Size, string> = {
  sm: "text-xs px-2.5 py-1.5 gap-1.5 rounded-md",
  md: "text-sm px-3.5 py-2 gap-2 rounded-md",
  lg: "text-sm px-4 py-2.5 gap-2 rounded-lg font-semibold",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    pending = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    disabled,
    className = "",
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || pending;
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={[
        "inline-flex items-center justify-center font-medium whitespace-nowrap",
        "transition-[background-color,border-color,transform,box-shadow] duration-150",
        "active:scale-[0.98]",
        "disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100",
        VARIANT[variant],
        SIZE[size],
        fullWidth ? "w-full" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {pending ? (
        <Spinner size={size === "sm" ? 12 : 14} />
      ) : (
        iconLeft && <span className="shrink-0 inline-flex">{iconLeft}</span>
      )}
      <span>{children}</span>
      {!pending && iconRight && (
        <span className="shrink-0 inline-flex">{iconRight}</span>
      )}
    </button>
  );
});

export default Button;
